// Stocky が公式 CSV を出せない仕入先系データを、手作り/別システム CSV から
// 復元する fallback。API コピーが使えない場合、または発注書履歴に現れず API
// コピーで推定できなかった商品↔仕入先の組み合わせだけに使う。
import { Banner, BlockStack, Text } from "@shopify/polaris";

import {
  applyManualSupplierCsv,
  applyManualSupplierLinkCsv,
  previewManualSupplierCsv,
  previewManualSupplierLinkCsv,
  type ManualSupplierCsvResult,
  type ManualSupplierLinkCsvResult,
} from "../../api/stocky_manual_supplier_import";
import { CsvImportPanel } from "../../components/CsvImportPanel";
import type { CsvImportSpec } from "../../components/csvImportSpec";
import { t } from "../../lib/i18n";

export const MANUAL_SUPPLIER_SPEC: CsvImportSpec<ManualSupplierCsvResult> = {
  id: "manual-suppliers-v2",
  title: "仕入先",
  description:
    "Stocky公式CSVはありません。手元で作る suppliers.csv の1行目に name を入れ、必要なら stocky_supplier_id、is_hidden、company_name、contact_name、contact_email、phone を追加します。name だけが必須です。空欄は既存値を消しません。",
  expectedFileName: "suppliers.csv（手元で作るCSV）",
  autoMapKnownColumns: true,
  fields: [
    { key: "name", label: "仕入先名の列（必須）", isRequired: true },
    { key: "stocky_supplier_id", label: "Stocky ID の列", isRequired: false },
    { key: "is_hidden", label: "非表示（is_hidden）の列", isRequired: false },
    { key: "company_name", label: "会社名の列", isRequired: false },
    { key: "contact_name", label: "担当者名の列", isRequired: false },
    { key: "contact_email", label: "メールアドレスの列", isRequired: false },
    { key: "phone", label: "電話番号の列", isRequired: false },
  ],
  readyHint: "仕入先名の列を選ぶと取り込めます。",
  preview: previewManualSupplierCsv,
  apply: applyManualSupplierCsv,
  overwriteNoun: "仕入先情報",
  overwriteCount: (result) => result.updated,
  writtenCount: (result) => result.created + result.updated,
  summarize: (result) =>
    t(
      "有効 {valid} 行・新規 {created} 件・更新 {updated} 件・変更なし {unchanged} 件・除外 {skipped} 行",
      {
        valid: result.valid_rows,
        created: result.created,
        updated: result.updated,
        unchanged: result.unchanged,
        skipped: result.skipped_row_count,
      },
    ),
  resultTitle: (result) =>
    t(
      "仕入先を新規 {created} 件・更新 {updated} 件取り込みました（変更なし {unchanged} 件）",
      {
        created: result.created,
        updated: result.updated,
        unchanged: result.unchanged,
      },
    ),
  unmatchedSkus: () => [],
  diagnostics: (result) => [
    {
      count: result.ambiguous_name_count,
      items: result.ambiguous_names,
      confirmTemplate:
        "同じ正規化名の仕入先が複数あるため特定できない仕入先名: {count} 件（該当行は取り込まれません）。",
      resultTemplate:
        "同じ正規化名の仕入先が複数あり特定できなかった仕入先名（{count} 件）: {values}",
    },
  ],
};

export const MANUAL_SUPPLIER_LINK_SPEC: CsvImportSpec<ManualSupplierLinkCsvResult> =
  {
    id: "manual-supplier-links-v2",
    title: "商品と仕入先の紐付け",
    description:
      "Stocky公式CSVはありません。手元で作る supplier_links.csv の1行目に sku,supplier_name を入れ、必要なら is_preferred,unit_cost,pack_size,min_order_qty,lead_time_days を追加します。SKUと仕入先名が必須です。CSVにない既存の紐付けは削除しません。",
    expectedFileName: "supplier_links.csv（手元で作るCSV）",
    autoMapKnownColumns: true,
    fields: [
      { key: "sku", label: "SKU の列（必須）", isRequired: true },
      { key: "supplier_name", label: "仕入先名の列（必須）", isRequired: true },
      {
        key: "is_preferred",
        label: "優先仕入先（is_preferred）の列",
        isRequired: false,
      },
      { key: "unit_cost", label: "仕入単価の列", isRequired: false },
      {
        key: "pack_size",
        label: "入数（1ケースの個数）の列",
        isRequired: false,
      },
      {
        key: "min_order_qty",
        label: "最低発注数量（MOQ）の列",
        isRequired: false,
      },
      {
        key: "lead_time_days",
        label: "リードタイム（日数）の列",
        isRequired: false,
      },
    ],
    readyHint: "SKU の列と仕入先名の列を選ぶと取り込めます。",
    preview: previewManualSupplierLinkCsv,
    apply: applyManualSupplierLinkCsv,
    overwriteNoun: "仕入条件または優先設定",
    overwriteCount: (result) =>
      result.condition_values_overwritten + result.preferred_changed,
    writtenCount: (result) =>
      result.links_created +
      result.links_updated +
      result.suppliers_created +
      result.preferred_created +
      result.preferred_changed,
    summarize: (result) =>
      t(
        "有効 {valid} 行・除外 {skipped} 行 / 対象商品 {linked} 件 / リンク: 新規 {linksCreated} 件・更新 {linksUpdated} 件・変更なし {linksUnchanged} 件 / 仕入先自動作成 {suppliersCreated} 件 / 条件書込 {conditionsWritten} 件（既存値上書き {overwritten} セル） / 優先仕入先: 新規 {preferredCreated} 件・変更または修復 {preferredChanged} 件・保持 {preferredPreserved} 件",
        {
          valid: result.valid_rows,
          skipped: result.skipped_row_count,
          linked: result.linked_variants,
          linksCreated: result.links_created,
          linksUpdated: result.links_updated,
          linksUnchanged: result.links_unchanged,
          suppliersCreated: result.suppliers_created,
          conditionsWritten: result.conditions_written,
          overwritten: result.condition_values_overwritten,
          preferredCreated: result.preferred_created,
          preferredChanged: result.preferred_changed,
          preferredPreserved: result.preferred_preserved,
        },
      ),
    resultTitle: (result) =>
      t(
        "商品と仕入先のリンクを新規 {created} 件・更新 {updated} 件取り込みました",
        { created: result.links_created, updated: result.links_updated },
      ),
    unmatchedSkus: (result) => result.unmatched_skus,
    diagnostics: (result) => [
      {
        count: result.ambiguous_sku_count,
        items: result.ambiguous_skus,
        confirmTemplate:
          "同じ正規化 SKU の商品が複数あるため特定できない SKU: {count} 件（該当行は取り込まれません）。",
        resultTemplate:
          "同じ正規化 SKU の商品が複数あり特定できなかった SKU（{count} 件）: {values}",
      },
      {
        count: result.ambiguous_supplier_name_count,
        items: result.ambiguous_supplier_names,
        confirmTemplate:
          "同じ正規化名の仕入先が複数あるため特定できない仕入先名: {count} 件（該当行は取り込まれません）。",
        resultTemplate:
          "同じ正規化名の仕入先が複数あり特定できなかった仕入先名（{count} 件）: {values}",
      },
    ],
  };

interface Props {
  onImported: () => void;
}

export function ManualSupplierFallback({ onImported }: Props) {
  return (
    <BlockStack gap="400">
      <Banner tone="warning" title={t("Stockyから書き出せる仕入先CSVはありません")}>
        <BlockStack gap="200">
          <Text as="p">
            {t(
              "下の2ファイルはStockyからダウンロードするCSVではありません。公式エクスポートに対象ファイルがなく、手元の情報で補う場合だけ作成してください。",
            )}
          </Text>
          <Text as="p">
            {t(
              "先に suppliers.csv、次に supplier_links.csv を選びます。各欄に必要なファイル名と1行目の列名を表示しています。",
            )}
          </Text>
        </BlockStack>
      </Banner>

      <CsvImportPanel spec={MANUAL_SUPPLIER_SPEC} onImported={onImported} />
      <CsvImportPanel
        spec={MANUAL_SUPPLIER_LINK_SPEC}
        onImported={onImported}
      />
    </BlockStack>
  );
}
